import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateScorePercentage } from "@/utils/score-utils";

export interface TestAnalytics {
  totalResponses: number;
  completedResponses: number;
  averageAIScore: number | null;
  averageTimeToComplete: number | null; // in minutes
  shortlistedCount: number;
  hiredCount: number;
}

export const useTestAnalytics = (testId: string) => {
  return useQuery({
    queryKey: ['test-analytics', testId],
    queryFn: async (): Promise<TestAnalytics> => {
      if (!testId) {
        return {
          totalResponses: 0,
          completedResponses: 0,
          averageAIScore: null,
          averageTimeToComplete: null,
          shortlistedCount: 0,
          hiredCount: 0,
        };
      }

      // Fetch all test responses for this test (excluding abandoned)
      const { data: responses, error } = await supabase
        .from('test_responses')
        .select(`
          id,
          status,
          score,
          total_ai_score,
          started_at,
          completed_at,
          recruitment_status,
          question_responses!inner(
            id,
            question_id,
            score,
            ai_feedback
          ),
          test:tests(
            id,
            questions!fk_test(
              id,
              marks
            )
          )
        `)
        .eq('test_id', testId)
        .in('status', ['completed', 'in_progress']);

      if (error) throw error;

      const totalResponses = responses.length;
      const completedResponses = responses.filter(r => r.status === 'completed').length;
      
      // Calculate recruitment status counts
      const shortlistedCount = responses.filter(r => r.recruitment_status === 'shortlisted').length;
      const hiredCount = responses.filter(r => r.recruitment_status === 'hired').length;

      // Calculate average AI score for completed responses only
      const completedWithScores = responses.filter(r => 
        r.status === 'completed' && r.question_responses && r.question_responses.length > 0
      );

      let averageAIScore: number | null = null;
      if (completedWithScores.length > 0) {
        const perResponseScores: number[] = [];

        completedWithScores.forEach((response: any) => {
          // If overall score exists on the response (stored as percentage 0-100), use it
          if (typeof response.score === 'number' && isFinite(response.score)) {
            perResponseScores.push(response.score);
            return;
          }

          // Otherwise, calculate from individual question scores normalized by marks
          const qMarksById: Record<string, number> = {};
          if (response.test?.questions) {
            response.test.questions.forEach((q: any) => {
              qMarksById[q.id] = q.marks ?? 1;
            });
          }

          // Use stored total_ai_score if available
          const aiScore = response.total_ai_score || 0;
          const totalPossible = response.test?.questions?.reduce((sum: number, q: any) => sum + (q.marks || 1), 0) || 1;
          
          if (aiScore > 0) {
            const percentage = Math.round((aiScore / totalPossible) * 100);
            perResponseScores.push(percentage);
          } else {
            // Fallback to individual question calculation if total_ai_score is 0
            let earned = 0;
            let possible = 0;
            response.question_responses.forEach((qr: any) => {
              const score = qr.ai_feedback?.score ?? qr.score;
              const max = qMarksById[qr.question_id] ?? 1;
              if (typeof score === 'number' && isFinite(score)) {
                earned += score;
              }
              possible += max;
            });
            
            if (possible > 0) {
              const percent = calculateScorePercentage(earned, possible);
              perResponseScores.push(percent);
            }
          }
        });

        if (perResponseScores.length > 0) {
          averageAIScore = Math.round(
            perResponseScores.reduce((sum, score) => sum + score, 0) / perResponseScores.length
          );
        }
      }

      // Calculate average time-to-complete for completed responses
      const completedWithTimes = responses.filter(r => 
        r.status === 'completed' && r.started_at && r.completed_at
      );

      let averageTimeToComplete: number | null = null;
      if (completedWithTimes.length > 0) {
        const totalMinutes = completedWithTimes.reduce((sum, response) => {
          const startTime = new Date(response.started_at).getTime();
          const endTime = new Date(response.completed_at).getTime();
          const durationMinutes = (endTime - startTime) / (1000 * 60); // Convert to minutes
          return sum + durationMinutes;
        }, 0);
        
        averageTimeToComplete = Math.round(totalMinutes / completedWithTimes.length);
      }

      return {
        totalResponses,
        completedResponses,
        averageAIScore,
        averageTimeToComplete,
        shortlistedCount,
        hiredCount,
      };
    },
    enabled: !!testId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};